import { useCallback, useEffect, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import type { SpenderScope } from "@/components/SpenderFilter";

export type LookbackPeriod = components["schemas"]["LookbackPeriod"];
export type ExpenseSpendReport = components["schemas"]["ExpenseSpendReport"];
export type ExpenseComplianceReport = components["schemas"]["ExpenseComplianceReport"];
export type ExpenseVatReport = components["schemas"]["ExpenseVatReport"];
export type ExpenseApprovalReport = components["schemas"]["ExpenseApprovalReport"];

/** One report's own fetch state, so a failing report blanks only its own cards. */
export interface ReportState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

type ReportPath =
  | "/v1/expenses/reports/spend"
  | "/v1/expenses/reports/compliance"
  | "/v1/expenses/reports/vat"
  | "/v1/expenses/reports/approvals";

function useReport<T>(path: ReportPath, period: LookbackPeriod, scope: SpenderScope, failure: string): ReportState<T> {
  const api = useDolfinClient();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The scope object is rebuilt on every render; refetch only when whose spend actually changes.
  const scopeKey = scope.kind === "person" ? `person:${scope.userId}` : scope.kind;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET(path, {
        params: {
          query: {
            Period: period,
            Mine: scope.kind === "mine" || undefined,
            SpenderUserId: scope.kind === "person" ? scope.userId : undefined,
          },
        },
      })
      .then(({ data: body, error: problem, response }) => {
        if (cancelled) return;
        if (problem || !body) {
          // A server fault's title ("Internal Server Error") tells the reader nothing; a 4xx says what to fix.
          setError(response.status >= 500 ? failure : apiErrorMessage(problem, failure));
          return;
        }
        setData(body as T);
      })
      .catch(() => {
        if (!cancelled) setError(failure);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, path, period, scopeKey, failure, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { data, loading, error, retry };
}

export function useExpenseReports(period: LookbackPeriod, scope: SpenderScope) {
  return {
    spend: useReport<ExpenseSpendReport>("/v1/expenses/reports/spend", period, scope, "Couldn't load spend"),
    compliance: useReport<ExpenseComplianceReport>(
      "/v1/expenses/reports/compliance",
      period,
      scope,
      "Couldn't load receipts and close status",
    ),
    vat: useReport<ExpenseVatReport>("/v1/expenses/reports/vat", period, scope, "Couldn't load VAT"),
    approvals: useReport<ExpenseApprovalReport>(
      "/v1/expenses/reports/approvals",
      period,
      scope,
      "Couldn't load approvals",
    ),
  };
}
